<template>
  <div>
    <!-- Hover tooltip: positioned near the seat -->
    <div
      v-if="visible && state === 'hover'"
      ref="card"
      class="seat-detail-card seat-detail-card--floating"
      :style="computedStyle"
      @click.stop
    >
      <div class="card-content">
        <div class="card-title-row">
          <span class="ticket-name">{{ workflowLabel(ticketName) }}</span>
          <span class="ticket-price">{{ workflowLabel(formattedPrice) }}</span>
        </div>
        <div v-if="detailCells.length" class="details-table">
          <div class="details-header">
            <span class="details-label">{{ workflowLabel(workflowCustomerLabel('details', {}, $t('details'))) }}</span>
          </div>
          <div class="details-row">
            <div v-for="(cell, i) in detailCells" :key="i" :class="['detail-cell', cell.border]">
              <span class="detail-cell-label">{{ workflowLabel(cell.label) }}</span>
              <span class="detail-cell-value">{{ workflowLabel(cell.value) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Click dialog: centered on screen -->
    <v-dialog v-model="dialogOpen" max-width="360" persistent content-class="seat-detail-dialog">
      <div class="card-content" @click.stop>
        <div class="card-title-row">
          <span class="ticket-name">{{ workflowLabel(ticketName) }}</span>
          <span class="ticket-price">{{ workflowLabel(formattedPrice) }}</span>
        </div>

        <div v-if="description" class="card-description">
          <p ref="descText" :class="['description-text', { expanded: descriptionExpanded }]">
            {{ workflowLabel(description) }}
          </p>
          <button
            v-if="descriptionTruncated || descriptionExpanded"
            class="read-more-btn"
            @click="toggleDescription"
          >
            <span>{{ workflowLabel(descriptionExpanded ? workflowCustomerLabel('readLess', {}, $t('readLess')) : workflowCustomerLabel('readMore', {}, $t('readMore'))) }}</span>
            <v-icon :size="16" color="#333">
              {{ descriptionExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </v-icon>
          </button>
        </div>

        <div class="details-table">
          <div class="details-header">
            <span class="details-label">{{ workflowLabel(workflowCustomerLabel('details', {}, $t('details'))) }}</span>
          </div>
          <div class="details-row">
            <div v-for="(cell, i) in detailCells" :key="i" :class="['detail-cell', cell.border]">
              <span class="detail-cell-label">{{ workflowLabel(cell.label) }}</span>
              <span class="detail-cell-value">{{ workflowLabel(cell.value) }}</span>
            </div>
          </div>
        </div>

        <div v-if="showEscortQuestion && state === 'click'" class="escort-question">
          <span class="escort-question-text">{{ workflowLabel(workflowCustomerLabel('escortQuestion', {}, $t('escortQuestion'))) }}</span>
          <div class="escort-options">
            <label class="escort-option">
              <input
                v-model="escortSelected"
                class="escort-option-input"
                type="radio"
                name="escort-answer"
                :value="true"
              >
              <span class="escort-radio" />
              <span class="escort-option-label">{{ workflowLabel(workflowCustomerLabel('escortYes', {}, $t('escortYes'))) }}</span>
            </label>
            <label class="escort-option">
              <input
                v-model="escortSelected"
                class="escort-option-input"
                type="radio"
                name="escort-answer"
                :value="false"
              >
              <span class="escort-radio" />
              <span class="escort-option-label">{{ workflowLabel(workflowCustomerLabel('escortNo', {}, $t('escortNo'))) }}</span>
            </label>
          </div>
        </div>

        <div class="card-actions">
          <button class="btn-cancel" @click="$emit('cancel')">
            {{ workflowLabel(state === 'added' ? workflowCustomerLabel('close', {}, $t('close')) : workflowCustomerLabel('cancel', {}, $t('cancel'))) }}
          </button>
          <div v-if="state === 'added'" class="card-added">
            <v-icon :size="24" color="#55A35D">check_circle</v-icon>
            <span class="added-text">{{ workflowLabel(workflowCustomerLabel('addedToCart', {}, $t('addedToCart'))) }}</span>
          </div>
          <button
            v-else
            class="btn-add"
            @click="$emit('add', { escort: showEscortQuestion && escortSelected })"
          >
            {{ workflowLabel(workflowCustomerLabel('add', {}, $t('add'))) }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
const GAP = 14
const EDGE_PAD = 10
const CARD_MAX_W = 327
const MIN_FIT = 180

export default {
  mixins: [WorkflowDisplay],
  name: 'seat-detail-card',
  props: {
    visible: { type: Boolean, default: false },
    state: {
      type: String,
      default: 'hover',
      validator: v => ['hover', 'click', 'added'].includes(v),
    },
    seatId: { type: String, default: '' },
    ticketName: { type: String, default: '' },
    price: { type: [Number, String], default: 0 },
    currency: { type: String, default: 'EUR' },
    description: { type: String, default: '' },
    type: { type: String, default: 'regular' },
    tableCapacity: { type: String, default: '' },
    anchorX: { type: Number, default: 0 },
    anchorY: { type: Number, default: 0 },
    showEscortQuestion: { type: Boolean, default: false },
  },
  data() {
    return {
      descriptionExpanded: false,
      descriptionTruncated: false,
      escortSelected: false,
      placement: { top: 0, left: 0, maxHeight: null, maxWidth: null },
      autoCloseTimer: null,
    }
  },
  computed: {
    formattedPrice() {
      return `${this.price} ${this.currency}`
    },
    seatDetails() {
      return this.parseSeatId(this.seatId)
    },
    detailCells() {
      const d = this.seatDetails
      const cells = []

      if (this.type === 'table') {
        cells.push({ label: this.workflowCustomerLabel('tableNumber', {}, this.$t('tableNumber')), value: d.seat || '-' })
        if (this.tableCapacity) {
          cells.push({ label: this.workflowCustomerLabel('capacity', {}, this.$t('capacity')), value: this.tableCapacity })
        }
      } else if (this.type === 'table-row') {
        if (d.section) cells.push({ label: this.workflowCustomerLabel('section', {}, this.$t('section')), value: d.section })
        if (d.row) cells.push({ label: this.workflowCustomerLabel('tableNumber', {}, this.$t('tableNumber')), value: d.row })
      } else if (this.type === 'regular') {
        if (d.section) cells.push({ label: this.workflowCustomerLabel('section', {}, this.$t('section')), value: d.section })
        if (d.row) cells.push({ label: this.workflowCustomerLabel('row', {}, this.$t('row')), value: d.row })
        if (d.seat) cells.push({ label: this.workflowCustomerLabel('seat', {}, this.$t('seat')), value: d.seat })
      }

      return cells.map((cell, i) => ({
        label: cell.label,
        value: cell.value,
        border: i > 0 ? 'border-left' : '',
      }))
    },
    dialogOpen: {
      get() {
        return this.visible && (this.state === 'click' || this.state === 'added')
      },
      set() {},
    },
    computedStyle() {
      const s = {
        top: this.placement.top + 'px',
        left: this.placement.left + 'px',
      }
      if (this.placement.maxWidth) {
        s.maxWidth = this.placement.maxWidth + 'px'
      }
      if (this.placement.maxHeight) {
        s.maxHeight = this.placement.maxHeight + 'px'
        s.overflowY = 'auto'
      }

      return s
    },
  },
  watch: {
    visible(val) {
      if (val && this.state === 'hover') {
        this.descriptionExpanded = false
        this.$nextTick(this.reposition)
      }
    },
    // the escort answer is per seat, never carried over to the next one
    seatId() {
      this.escortSelected = false
    },
    showEscortQuestion() {
      this.escortSelected = false
    },
    state(val) {
      clearTimeout(this.autoCloseTimer)
      if (val === 'hover') {
        this.$nextTick(this.reposition)
      }
      if (val === 'click' || val === 'added') {
        this.descriptionExpanded = false
        this.$nextTick(this.checkDescriptionTruncated)
      }
      if (val === 'added') {
        this.autoCloseTimer = setTimeout(() => {
          this.$emit('cancel')
        }, 1000)
      }
    },
    anchorX() {
      if (this.state === 'hover') this.$nextTick(this.reposition)
    },
    anchorY() {
      if (this.state === 'hover') this.$nextTick(this.reposition)
    },
    seatId() {
      this.descriptionExpanded = false
      this.$nextTick(this.checkDescriptionTruncated)
    },
  },
  beforeDestroy() {
    clearTimeout(this.autoCloseTimer)
  },
  methods: {
    checkDescriptionTruncated() {
      const self = this
      const check = function () {
        const el = self.$refs.descText
        if (!el) {
          self.descriptionTruncated = false

          return
        }
        self.descriptionTruncated = el.scrollWidth > el.clientWidth
      }
      check()
      setTimeout(check, 150)
    },
    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded
    },
    parseSeatId(id) {
      if (!id) return { section: '', row: '', seat: '' }
      const match = id.match(/^Section-([^_]*)_Row-([^_]*)_Seat-([^_]*)$/)
      if (!match) return { section: '', row: '', seat: '' }

      return { section: match[1], row: match[2], seat: match[3] }
    },
    reposition() {
      const el = this.$refs.card
      if (!el) return

      const vw = window.innerWidth || document.documentElement.clientWidth
      const vh = window.innerHeight || document.documentElement.clientHeight
      const maxCardWidth = Math.min(CARD_MAX_W, Math.max(vw - EDGE_PAD * 2, 0))

      el.style.maxHeight = 'none'
      el.style.maxWidth = maxCardWidth + 'px'
      el.style.overflowY = 'visible'

      const cardW = el.offsetWidth
      const cardH = el.offsetHeight
      const ax = this.anchorX
      const ay = this.anchorY

      const above = ay - GAP
      const below = vh - ay - GAP
      const toLeft = ax - GAP
      const toRight = vw - ax - GAP

      let top, left, mh, mw
      mh = null
      mw = null

      if (above >= cardH) {
        top = ay - GAP - cardH
        left = this.clampH(ax, cardW, vw)
        mh = above - EDGE_PAD
      } else if (below >= cardH) {
        top = ay + GAP
        left = this.clampH(ax, cardW, vw)
        mh = below - EDGE_PAD
      } else if (toLeft >= cardW) {
        left = ax - GAP - cardW
        top = this.clampV(ay, cardH, vh)
        mh = vh - EDGE_PAD * 2
      } else if (toRight >= cardW) {
        left = ax + GAP
        top = this.clampV(ay, cardH, vh)
        mh = vh - EDGE_PAD * 2
      } else {
        const spaces = [
          { dir: 'above', size: above },
          { dir: 'below', size: below },
          { dir: 'left', size: toLeft },
          { dir: 'right', size: toRight },
        ]
        spaces.sort((a, b) => b.size - a.size)
        const best = spaces[0]

        if (best.dir === 'above') {
          mh = Math.max(above - EDGE_PAD, MIN_FIT)
          top = Math.max(EDGE_PAD, ay - GAP - Math.min(cardH, mh))
          left = this.clampH(ax, cardW, vw)
        } else if (best.dir === 'below') {
          mh = Math.max(below - EDGE_PAD, MIN_FIT)
          top = ay + GAP
          left = this.clampH(ax, cardW, vw)
        } else if (best.dir === 'left') {
          mw = Math.max(toLeft - EDGE_PAD, MIN_FIT)
          left = Math.max(EDGE_PAD, ax - GAP - Math.min(cardW, mw))
          top = this.clampV(ay, cardH, vh)
        } else {
          mw = Math.max(toRight - EDGE_PAD, MIN_FIT)
          left = ax + GAP
          top = this.clampV(ay, cardH, vh)
        }
      }

      this.placement = { top, left, maxHeight: mh, maxWidth: mw }
    },
    clampH(ax, cardW, vw) {
      let left = ax - cardW / 2
      if (left < EDGE_PAD) left = EDGE_PAD
      if (left + cardW > vw - EDGE_PAD) left = vw - EDGE_PAD - cardW

      return Math.max(EDGE_PAD, left)
    },
    clampV(ay, cardH, vh) {
      let top = ay - cardH / 2
      if (top < EDGE_PAD) top = EDGE_PAD
      if (top + cardH > vh - EDGE_PAD) top = vh - EDGE_PAD - cardH

      return Math.max(EDGE_PAD, top)
    },
  },
}
</script>

<style scoped>
.seat-detail-card--floating {
  position: fixed;
  z-index: 10000;
  width: 100%;
  max-width: calc(100vw - 20px);
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  pointer-events: auto;
  background: #f5f5f5;
  border: 1px solid rgba(51, 51, 51, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.seat-detail-card--floating .card-content {
  border-radius: 0;
}

.card-content {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 16px;
  -webkit-box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-title-row {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  min-height: 24px;
  gap: 4px;
}

.ticket-name {
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-price {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #333;
  text-transform: uppercase;
  letter-spacing: -0.48px;
  -webkit-font-feature-settings: 'lnum', 'pnum';
  font-feature-settings: 'lnum', 'pnum';
  font-variant-numeric: lining-nums proportional-nums;
  white-space: nowrap;
}

.card-description {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 4px;
}

.description-text {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.description-text.expanded {
  white-space: normal;
  overflow: visible;
}

.read-more-btn {
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.read-more-btn span {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.18px;
  -webkit-font-feature-settings: 'lnum', 'pnum';
  font-feature-settings: 'lnum', 'pnum';
  font-variant-numeric: lining-nums proportional-nums;
  line-height: 1.2;
}

.details-table {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
}

.details-header {
  background: #fff;
  border-bottom: 1px solid #f5f5f5;
  padding: 8px 16px;
  height: 40px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.details-label {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666;
}

.details-row {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  border-radius: 0 0 4px 4px;
  overflow: hidden;
}

.detail-cell {
  -webkit-box-flex: 1;
  -ms-flex: 1 1 0%;
  flex: 1 1 0%;
  min-width: 60px;
  background: #fff;
  padding: 8px 12px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 4px;
  word-break: break-word;
}

.detail-cell.border-right {
  border-right: 1px solid #f5f5f5;
}

.detail-cell.border-left {
  border-left: 1px solid #f5f5f5;
}

.detail-cell-label {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666;
}

.detail-cell-value {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
}

.escort-question {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.escort-question-text {
  display: block;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666666;
  margin-bottom: 8px;
}

.escort-options {
  display: flex;
  gap: 24px;
}

.escort-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.escort-option-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}

.escort-radio {
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.escort-radio::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  right: 3px;
  bottom: 3px;
  box-sizing: border-box;
  border: 2px solid rgba(151, 151, 151, 0.5);
  border-radius: 50%;
}

.escort-option-input:checked + .escort-radio::before {
  border-color: #0277bd;
}

.escort-option-input:checked + .escort-radio::after {
  content: '';
  position: absolute;
  top: 7px;
  left: 7px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #0277bd;
}

.escort-option-input:focus-visible + .escort-radio::before {
  box-shadow: 0 0 0 3px rgba(2, 119, 189, 0.25);
}

.escort-option-label {
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.24px;
  color: #333333;
}

.card-actions {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  gap: 8px;
}

.btn-cancel {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.24px;
  color: #333;
  background: none;
  border: none;
  padding: 6px 12px;
  border-radius: 24px;
  cursor: pointer;
  line-height: 1.2;
  -webkit-font-feature-settings: 'lnum', 'pnum';
  font-feature-settings: 'lnum', 'pnum';
  font-variant-numeric: lining-nums proportional-nums;
}

.btn-cancel:hover {
  background: rgba(0, 0, 0, 0.04);
}

.btn-add {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.24px;
  color: #fff;
  background: #333;
  border: none;
  padding: 6px 12px;
  height: 30px;
  border-radius: 24px;
  cursor: pointer;
  line-height: 1.2;
  -webkit-font-feature-settings: 'lnum', 'pnum';
  font-feature-settings: 'lnum', 'pnum';
  font-variant-numeric: lining-nums proportional-nums;
}

.btn-add:hover {
  background: #444;
}

.card-added {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  gap: 8px;
  height: 24px;
}

.added-text {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
}

@media (max-width: 360px) {
  .card-content {
    padding: 12px;
    gap: 12px;
  }

  .detail-cell {
    padding: 6px 8px;
  }

  .detail-cell-label {
    font-size: 13px;
  }

  .detail-cell-value {
    font-size: 15px;
  }

  .btn-cancel,
  .btn-add {
    font-size: 14px;
    padding: 6px 10px;
  }
}
</style>

<style>
.seat-detail-dialog {
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  background: transparent !important;
  max-height: calc(100vh - 24px) !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}
</style>
